import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  UnitTransferDocumentFulfilmentModeEnumType,
  UnitTransferDocumentStatusEnumType,
  UnitTransferDocumentTypeEnumType,
  UnitTransferRequestStatusEnumType,
} from '__generated__/graphql';
import { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import ErrorMessage from 'components/ErrorMessage';

import Alert from 'primitives/Alert';
import Badge from 'primitives/Badge';
import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';

import { getUser } from 'utils/auth';
import unitTransferDocumentTypeToReadable from 'utils/enums/unit-transfer-document-type-to-readable';

import getUnitTransferRequestTabs from '../tabs';
import ChecklistProgress from './ChecklistProgress';
import DocumentChecklistItem, { ChecklistDocument, getStatusBadge } from './DocumentChecklistItem';

const UNIT_TRANSFER_DOCUMENTS_QUERY = gql(`
  query UnitTransferDocuments($id: ID!) {
    unitTransferRequest(id: $id) {
      id
      status
      transferor {
        id
      }
      documents {
        id
        type
        name
        status
        fulfilmentMode
        isRequired
        rejectionReason
        url
        blankTemplateUrl
      }
      documentChecklistProgress {
        requiredCount
        fulfilledCount
        approvedCount
      }
    }
  }
`);

const SUBMIT_UNIT_TRANSFER_DOCUMENTS_FOR_REVIEW_MUTATION = gql(`
  mutation SubmitUnitTransferDocumentsForReview($requestId: ID!) {
    submitUnitTransferDocumentsForReview(requestId: $requestId) {
      id
      status
    }
  }
`);

const UPLOADED_STATUSES = [
  UnitTransferDocumentStatusEnumType.Uploaded,
  UnitTransferDocumentStatusEnumType.AiChecked,
  UnitTransferDocumentStatusEnumType.Approved,
];

// Mirrors the backend's SUBMITTABLE/REMOVABLE/UPLOADABLE_REQUEST_STATUSES — all three are the
// same three statuses, so one flag gates submitting, uploading and removing alike. Once the
// request has moved past these (into review, verified, signing, etc.), the checklist is closed.
const DOCUMENT_CHECKLIST_OPEN_STATUSES = [
  UnitTransferRequestStatusEnumType.ConditionsIssued,
  UnitTransferRequestStatusEnumType.PendingDocuments,
  UnitTransferRequestStatusEnumType.DocumentsIncomplete,
];

// One row per document in the side list, grouped under its audience heading (who's responsible
// for it) rather than the flat list the admin app uses — the investor needs to know at a glance
// which documents are theirs to act on versus just context.
const DocumentNavItem: FC<{
  document: ChecklistDocument;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ document, isSelected, onSelect }) => {
  const { label, color } = getStatusBadge(document);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left px-3 py-2.5 rounded-md border-l-4 border-t border-r border-b shadow-sm transition-colors ${
        isSelected
          ? 'bg-indigo-50 border-l-indigo-600 border-t-indigo-100 border-r-indigo-100 border-b-indigo-100'
          : 'bg-white border-l-transparent border-t-gray-200 border-r-gray-200 border-b-gray-200 hover:border-l-indigo-200 hover:bg-gray-50'
      }`}
    >
      <p className={`text-sm font-medium ${isSelected ? 'text-indigo-900' : 'text-gray-900'}`}>
        {unitTransferDocumentTypeToReadable(document.type)}
        {!document.isRequired && <span className="font-normal text-gray-400"> (optional)</span>}
      </p>
      <div className="mt-1.5">
        <Badge label={label} color={color} showIndicator />
      </div>
    </button>
  );
};

const UnitTransferDocuments: FC = () => {
  const { requestId } = useParams<{ requestId: string }>() as { requestId: string };
  const navigate = useNavigate();
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery(UNIT_TRANSFER_DOCUMENTS_QUERY, {
    variables: { id: requestId },
  });

  const [submitForReview, { loading: submitting, error: submitError }] = useMutation(
    SUBMIT_UNIT_TRANSFER_DOCUMENTS_FOR_REVIEW_MUTATION
  );

  const renderContent = () => {
    if (loading && !data) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    // A superseded row is a file that was already replaced. The admin app filters these out;
    // the investor was still being shown them as if they were live checklist items.
    const documents: ChecklistDocument[] = data.unitTransferRequest.documents.filter(
      document => document.status !== UnitTransferDocumentStatusEnumType.Superseded
    );

    // The checklist is generated when ops issues conditions (UNDER_REVIEW -> CONDITIONS_ISSUED) —
    // every earlier status has zero unit_transfer_document rows.
    if (documents.length === 0)
      return (
        <p className="text-sm text-gray-600">
          The document checklist isn&apos;t available yet — it appears once your transfer has been
          reviewed and conditions are issued.
        </p>
      );

    const groupFor = (mode: UnitTransferDocumentFulfilmentModeEnumType) =>
      documents.filter(document => document.fulfilmentMode === mode);

    // Grouped (and ordered) by audience, not by document — mirrors the three sections the page
    // used to render as separate cards. Empty groups drop out rather than showing a blank heading.
    const groups = [
      {
        key: 'investor',
        label: 'Your documents',
        documents: groupFor(UnitTransferDocumentFulfilmentModeEnumType.InvestorUpload),
      },
      {
        key: 'admin',
        label: "We'll provide these",
        documents: groupFor(UnitTransferDocumentFulfilmentModeEnumType.AdminUpload),
      },
      {
        key: 'signature',
        label: "You'll sign these later",
        documents: groupFor(UnitTransferDocumentFulfilmentModeEnumType.Signature),
      },
    ].filter(group => group.documents.length > 0);

    // Counts only what the INVESTOR is responsible for, mirroring the backend's submission gate.
    // documentChecklistProgress counts every required document including the ones AngelList
    // supplies (the Statement of Account) and the ones produced by e-signature, so driving the
    // button off it left the investor permanently one short and waiting on ops, while ops had no
    // reason to act until the investor was done.
    const requiredInvestorDocuments = groupFor(
      UnitTransferDocumentFulfilmentModeEnumType.InvestorUpload
    ).filter(document => document.isRequired);
    const uploadedCount = requiredInvestorDocuments.filter(document =>
      UPLOADED_STATUSES.includes(document.status)
    ).length;
    const canManageDocuments = DOCUMENT_CHECKLIST_OPEN_STATUSES.includes(
      data.unitTransferRequest.status
    );
    const isTransferor = data.unitTransferRequest.transferor.id === getUser().id;
    const canSubmit = canManageDocuments && uploadedCount >= requiredInvestorDocuments.length;

    const allDocumentsInOrder = groups.flatMap(group => group.documents);
    const selectedDocument =
      allDocumentsInOrder.find(document => document.id === selectedDocumentId) ??
      allDocumentsInOrder[0] ??
      null;

    return (
      <div className="space-y-4">
        {submitError ? <ErrorMessage error={submitError} type="alert" /> : null}

        {canManageDocuments && !canSubmit ? (
          <Alert
            type="warning"
            title="Upload every document in “Your documents” before submitting"
            message="Items we provide, or that arrive later as an e-signature, are not yours to upload — they will not hold you up."
          />
        ) : null}

        {!canManageDocuments ? (
          <Alert
            type="info"
            title="Documents already submitted"
            message="These documents have already been submitted for review — there's nothing more to do here."
          />
        ) : null}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1">
            {requiredInvestorDocuments.length > 0 ? (
              <ChecklistProgress
                completed={uploadedCount}
                total={requiredInvestorDocuments.length}
              />
            ) : null}
          </div>
          {canManageDocuments ? (
            <Button
              disabled={!canSubmit}
              loading={submitting}
              onClick={() =>
                submitForReview({ variables: { requestId } }).then(result => {
                  if (result.data?.submitUnitTransferDocumentsForReview) {
                    navigate(`/invest/unit-transfers/${requestId}`);
                  }
                })
              }
            >
              Submit for review
            </Button>
          ) : null}
        </div>

        {selectedDocument && (
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            <div className="flex flex-col gap-5 rounded-lg bg-gray-50 p-3 md:w-72 md:flex-shrink-0">
              {groups.map(group => (
                <div key={group.key}>
                  <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {group.label}
                  </p>
                  <div className="flex flex-col gap-2">
                    {group.documents.map(document => (
                      <DocumentNavItem
                        key={document.id}
                        document={document}
                        isSelected={selectedDocument.id === document.id}
                        onSelect={() => setSelectedDocumentId(document.id)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="min-w-0 flex-1">
              <DocumentChecklistItem
                document={selectedDocument}
                requestId={requestId}
                onChanged={() => refetch()}
                // The transferee can see the valuation report but only the transferor uploads it.
                canManageDocuments={
                  canManageDocuments &&
                  (isTransferor ||
                    selectedDocument.type !== UnitTransferDocumentTypeEnumType.ValuationReport)
                }
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <SidebarLayout>
      <SidebarLayoutNavigation
        title="Unit Transfer Documents"
        subTitle="Upload and track the documents required for this transfer"
        secondaryTabs={getUnitTransferRequestTabs(requestId)}
        onBackButtonClick={() => navigate(`/invest/unit-transfers/${requestId}`)}
      />
      <SidebarLayoutContent>{renderContent()}</SidebarLayoutContent>
    </SidebarLayout>
  );
};

export default UnitTransferDocuments;
